import { useEffect, MutableRefObject } from 'react';
import { eventStreamService } from 'core-roblox-utilities';
import { getElementWidth, isElementInWindow } from '../utils/browserUtils';
import { debounce } from '../utils/helperUtils';
import { TGameData } from '../types/bedev1Types';
import eventStreamConstants, {
  TOmniRecommendationsGameImpressions
} from '../constants/eventStreamConstants';

export const useGameImpressionCarouselTracker = <T extends HTMLElement, U extends HTMLElement>(
  carouselRef: MutableRefObject<T | null>,
  tileRef: MutableRefObject<U | null>,
  gameData: TGameData[] | undefined,
  metaData: Pick<
    TOmniRecommendationsGameImpressions,
    'sortPos' | 'gameSetTypeId' | 'gameSetTargetId' | 'pg'
  >
): void => {
  const calculateCarouselVisibility = (carouselElement: T | null): boolean => {
    if (carouselElement) {
      return isElementInWindow(carouselElement);
    }
    return false;
  };

  const calculateVisibleGameTiles = (
    carouselElement?: T | null,
    tileElement?: U | null
  ): [number, number] | undefined => {
    if (carouselElement && tileElement) {
      const { clientWidth: carouselDomRectWidth, scrollLeft } = carouselElement;
      const tileDomRectWidth = getElementWidth(tileElement);
      const unseenTiles = Math.round(scrollLeft / tileDomRectWidth);
      const totalTiles = Math.round((scrollLeft + carouselDomRectWidth) / tileDomRectWidth) - 1;
      return [unseenTiles, totalTiles];
    }
    return undefined;
  };

  const sendEvent = () => {
    const isCarouselVisible = calculateCarouselVisibility(carouselRef.current);
    const isCarouselTilesVisible = calculateVisibleGameTiles(carouselRef.current, tileRef.current);
    if (isCarouselVisible && isCarouselTilesVisible && gameData) {
      const [firstIndex, lastIndex] = isCarouselTilesVisible;
      const viewedGameData = gameData.slice(firstIndex, lastIndex + 1);
      const gameImpressionsParams = eventStreamConstants.gameImpressions({
        rootPlaceIds: viewedGameData.map(data => data.placeId),
        absPositions: viewedGameData.map((_, index) => firstIndex + index),
        ...metaData
      });
      eventStreamService.sendEvent(...gameImpressionsParams);
    }
  };

  const [onDebouncedEvent, cancelOnDebouncedEvent] = debounce(() => {
    sendEvent();
  });

  const onCleanUp = () => {
    cancelOnDebouncedEvent();
    window.removeEventListener('scroll', onDebouncedEvent);
    window.removeEventListener('resize', onDebouncedEvent);
    window.removeEventListener('beforeunload', onCleanUp);
  };

  useEffect(() => {
    window.addEventListener('scroll', onDebouncedEvent);
    window.addEventListener('resize', onDebouncedEvent);
    window.addEventListener('beforeunload', onCleanUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentCarouselRef = carouselRef.current;
    currentCarouselRef?.addEventListener('scroll', onDebouncedEvent);
    return () => {
      currentCarouselRef?.removeEventListener('scroll', onDebouncedEvent);
    };
  }, [carouselRef, onDebouncedEvent]);

  useEffect(() => {
    sendEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameData]);
};

export default useGameImpressionCarouselTracker;
